import { is, partial } from 'ramda';
import { makeXPathEngine } from '../../../utility/xmlHelpers';
import { getNodeValue, setNodeValue } from '../../../xmlUtility';
import { ERROR_FAILED_TO_SELECT_DESTINATION } from '../constants/errors';
import { paramResultDs, paramResultPath } from '../constants/parameterNames';
import { failure } from '../helpers/commandResult';
import {
    normalizeResult,
    populateResult,
} from '../helpers/commandResultHandling';
import {
    getBoolParamValue,
    getDoubleParamValue,
    getIntParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import {
    convertParameters,
    getCommandParameters,
    processCommandString,
    validateParameters,
} from '../helpers/parameter.processing';
import {
    getDataConnection,
    verifyDataConnectionExists,
} from '../helpers/qRules.dataConnection.helpers';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';
import { getUsageTextForCommand } from '../helpers/qRules.usageTextHelpers';
import { selectNodeFromXPathEngine } from '../helpers/qRules.utility';
import qRulesNamespaceResolver from '../helpers/qRulesNamespaceResolver';
import { DATA_SOURCE_NAME_QDABRA_RULES } from '../helpers/qdabraRulesDs.helpers';
import transformSource from '../helpers/transformSource';
import { registerViewNameTracker } from '../helpers/viewNameTracker';

const qd = window.qd;

const qr = qd.qRules;

const isFunction = is(Function);

function getCommandDefinition(commands, commandName, parameters) {
    if (!commandName) {
        return null;
    }

    const command = commands[commandName];

    if (!command) {
        return null;
    }

    return isFunction(command) ? command(parameters) : command;
}

const createDbxlXPathEngine = () => makeXPathEngine(qRulesNamespaceResolver);

const setNodeValues = async (nodes, value) => {
    for (const node of nodes) {
        await node.setValueAsync(value);
    }
};

const setDestinationNode = async (
    dataSources,
    destDsName,
    destXPath,
    resultValue,
) => {
    //if no destination xpath is provided, but a dest data source name is, error
    if (!destXPath) {
        throw new Error('An XPath must be provided for the destination node.');
    }

    //if dest is provided, set the node for that
    const destDs = getDataSource(dataSources, destDsName);

    const destNodes = getValidNodeSet(
        destDs,
        destXPath,
        `${ERROR_FAILED_TO_SELECT_DESTINATION} Result has been returned to the result node.`,
    );

    await setNodeValues(destNodes, resultValue);
};

function commonFunctions({
    parameters,
    dataSources,
    dataConnections,
    settings,
}) {
    // 2024-06-03 - Removed getNodes()

    // 2023-04-20 - Removed loadTransformOptions()

    // 2023-04-20 - Removed getTransform()

    // 3034-06-07 - Removed getTransformNew()

    function userName() {
        return settings.user;
    }

    // 2022-11-03 - Removed unused getValidDate()

    function getIntegerValue(value) {
        if (!value) {
            return null;
        }

        return Number(value);
    }

    // TODO: This seems to just be a pointless alias for getValidNode. Consider removing.
    function getPassword(dsnamepass, passwordXPath, error) {
        return getValidNode(
            getDataSource(dataSources, dsnamepass),
            passwordXPath,
            error,
        );
    }

    function getTwoDigitDateFormat(value) {
        return value > 9 ? value.toString() : '0' + value;
    }

    function createUniqueTimestamp() {
        var date = new Date(),
            year = date.getFullYear(),
            month = date.getMonth() + 1;

        return (
            year +
            getTwoDigitDateFormat(month) +
            getTwoDigitDateFormat(date.getDate()) +
            getTwoDigitDateFormat(date.getHours()) +
            getTwoDigitDateFormat(date.getMinutes()) +
            getTwoDigitDateFormat(date.getSeconds()) +
            getTwoDigitDateFormat(date.getMilliseconds())
        );
    }

    // commonFunctions interface
    return {
        // nodes
        getValidNode,
        getValidNodeSet,

        // parameters
        getBoolParamValue: partial(getBoolParamValue, [parameters]),
        getDoubleParamValue: partial(getDoubleParamValue, [parameters]),
        getIntParamValue: partial(getIntParamValue, [parameters]),
        getParamValue: partial(getParamValue, [parameters]),

        // data connections
        getDataConnection: partial(getDataConnection, [dataConnections]),

        // last uses are in SaveToSharePoint and GetListGuid
        verifyDataConnectionExists: partial(verifyDataConnectionExists, [
            dataConnections,
        ]),

        // data sources
        getDataSource: partial(getDataSource, [dataSources]),

        environment: {
            userName,
        },
        // only used in DateAdd
        getIntegerValue,
        // only used in Encrypt/Decrypt
        getPassword,
        createUniqueTimestamp, // used by SaveToNetwork - can be moved to module
    };
}

const validateAndExecuteCommand = async (
    commandName,
    command,
    parameterCollection,
    services,
) => {
    try {
        const optionalParameters = convertParameters(
            command.optionalParameters || [],
        );
        const requiredParameters = convertParameters(
            command.requiredParameters || [],
        );
        const commandParameters = getCommandParameters(
            optionalParameters,
            requiredParameters,
        );

        const usageText = getUsageTextForCommand(
            optionalParameters,
            requiredParameters,
            commandName,
        );

        validateParameters(parameterCollection, commandParameters, usageText);

        const execFunc = command.executeAsync || command.execute;

        if (!execFunc) {
            throw new Error('Command does not have an execution method.');
        }

        return await execFunc.call(command, services);
    } catch (e) {
        console.error('qRules command failure: ', e);

        return {
            result: '',
            success: false,
            error: e.message,
        };
    }
};

function utilities() {
    function getQueryNode(xPathEngine, dataSourceRoot, xPath, documentType) {
        const queryFields = selectNodeFromXPathEngine(
            xPathEngine,
            '/dfs:myFields/dfs:queryFields/tns:' + documentType,
            dataSourceRoot,
        );
        const node = queryFields
            ? selectNodeFromXPathEngine(xPathEngine, xPath, queryFields)
            : null;

        return node;
    }

    function getOrSetValue(node, value) {
        if (node) {
            if (typeof value === 'undefined') {
                return getNodeValue(node);
            }

            return setNodeValue(node, value);
        }

        return null;
    }

    function getOrSetNodeValue(
        xPathEngine,
        dataSourceRoot,
        documentType,
        xPath,
        nodeValue,
    ) {
        var queryNode = getQueryNode(
            xPathEngine,
            dataSourceRoot,
            xPath,
            documentType,
        );

        //TODO: Throw error?
        if (!queryNode) {
            return null;
        }

        return getOrSetValue(queryNode, nodeValue);
    }
    function submitDocumentAsUser(dsRoot, dataConn) {
        var dataSourceRoot = dsRoot,
            dataConnection = dataConn,
            xPathEngine = createDbxlXPathEngine(),
            documentType = 'SubmitDocumentAsUser';

        function docTypeName(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:docTypeName',
                nodeValue,
            );
        }

        function userName(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:submitAsUser',
                nodeValue,
            );
        }

        function xml(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:xml',
                nodeValue,
            );
        }

        function name(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:name',
                nodeValue,
            );
        }

        function author(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:author',
                nodeValue,
            );
        }

        function description(nodeValue) {
            getOrSetNodeValue(
                xPathEngine,
                dataSourceRoot,
                documentType,
                'tns:description',
                nodeValue,
            );
        }

        // 2023-10-05 - Removed unused success()

        function getError(exception) {
            if (exception) {
                return exception.message;
            }

            var errorNode = selectNodeFromXPathEngine(
                xPathEngine,
                '/dfs:myFields/dfs:dataFields/tns:SubmitDocumentAsUserResponse/tns:SubmitDocumentAsUserResult/tns:Errors',
                dataSourceRoot,
            );

            if (!errorNode || !getNodeValue(errorNode)) {
                return 'Error data unavailable';
            }

            return getNodeValue(errorNode);
        }

        function failureResult(exception) {
            return {
                success: false,
                error: getError(exception),
            };
        }

        function querySubmitDocumentAsUser(
            docType,
            user,
            xmlData,
            nameData,
            authorData,
            desc,
        ) {
            docTypeName(docType);
            userName(user);
            xml(xmlData);
            name(nameData);
            author(authorData);
            description(desc);

            var resultData = {
                docId: '',
                refId: '',
                success: false,
            };

            return dataConnection
                .executeAsync()
                .then(function () {
                    var docIdNode = selectNodeFromXPathEngine(
                        xPathEngine,
                        '/dfs:myFields/dfs:dataFields/tns:SubmitDocumentAsUserResponse/tns:docId',
                        dataSourceRoot,
                    );
                    //TODO:Throw error if node not present?

                    if (!docIdNode) {
                        return failureResult();
                    }

                    var docIdValue = getNodeValue(docIdNode);

                    if (docIdValue === '-1') {
                        return failureResult();
                    }

                    var refIdNode = selectNodeFromXPathEngine(
                        xPathEngine,
                        '/dfs:myFields/dfs:dataFields/tns:SubmitDocumentAsUserResponse/tns:refId',
                        dataSourceRoot,
                    );

                    if (!refIdNode) {
                        return failureResult();
                    }

                    return {
                        docId: docIdValue,
                        refId: getNodeValue(refIdNode),
                        success: true,
                    };
                })
                .catch(function (e) {
                    console.error('Encountered error submitting document', e);

                    resultData.docId = '-1';
                    return resultData;
                });
        }

        return {
            querySubmitDocumentAsUser,
        };
    }

    return {
        submitDocumentAsUser,
    };
}

// (CommandResult, DataSources, ParameterCollection) -> CommandResult
const checkAndSetDestNodeValues = async (
    commandResultData,
    dataSources,
    parameters,
) => {
    if (!commandResultData.success) {
        return commandResultData;
    }

    const destXPath = getParamValue(parameters, paramResultPath);
    const destDsName = getParamValue(parameters, paramResultDs);

    if (!(destXPath || destDsName)) {
        return commandResultData;
    }

    try {
        await setDestinationNode(
            dataSources,
            destDsName,
            destXPath,
            commandResultData.result,
        );

        return commandResultData;
    } catch (e) {
        return {
            ...commandResultData,
            error: e.message,
            success: false,
        };
    }
};

function qRules({
    dataConnections,
    dataSources,
    template,
    qfsAccess,
    xpathEngine,
    shpAccess,
    viewManager,
    sampleDataSource,
    settings,
    characterLimitSource,
    logging,
    services: providedServices = {},
}) {
    const commands = Object.assign({}, qr);

    const getParameterObject = (cf) => ({
        commonFunction: cf,
        commonFunctions: cf,
        dataConnections,
        dataSources,
        qfsAccess,
        shpAccess,
        template,
        utilities: utilities(),
        xpathEngine,
        sampleDataSource,
        characterLimitSource,
        logging,
    });

    const transforms = transformSource(template, settings.functions);

    const findCommand = (commandName, parameters) => {
        const commandDefinition = getCommandDefinition(
            commands,
            commandName,
            parameters,
        );

        if (commandDefinition) {
            return { commandDefinition };
        }

        const customName = 'Custom' + commandName;
        const customCommandAvailable = getCommandDefinition(
            commands,
            customName,
            parameters,
        );

        return {
            error: `Unsupported command: ${commandName}${
                customCommandAvailable ? ` - did you mean ${customName}?` : ''
            }`,
        };
    };

    // String -> CommandResult
    const executeCommandInnerAsync = async (commandString) => {
        const parsedCommand = processCommandString(commandString, dataSources);

        if (!parsedCommand.success) {
            return failure(parsedCommand.error);
        }

        const { parameters, commandName } = parsedCommand;

        const cf = commonFunctions({
            parameters,
            dataSources,
            dataConnections,
            template,
            viewManager,
            settings,
        });

        const parameterObject = getParameterObject(cf);
        const { commandDefinition, error: findCommandError } = findCommand(
            commandName,
            parameterObject,
        );

        if (findCommandError) {
            return failure(findCommandError);
        }

        const services = {
            ...providedServices,
            api: qfsAccess,
            sharePointAccess: shpAccess,
            dataConnections,
            dataSources,
            // parameters should be treated as a black box and accessed using
            // accessor functions since its implementation will likely change
            parameters,
            viewManager,
            template,
            transforms,
        };

        try {
            const resultObject = await validateAndExecuteCommand(
                commandName,
                commandDefinition,
                parameters,
                services,
            );

            const commandResultData = normalizeResult(resultObject);

            return await checkAndSetDestNodeValues(
                commandResultData,
                dataSources,
                parameters,
            );
        } catch (e) {
            console.error('Command failure: ', e);

            return failure(e.message);
        }
    };

    const executeCommandAsync = async (commandParams) => {
        const commandNode = commandParams.node;
        const commandString = commandNode.value();

        if (!commandString) {
            return;
        }

        await commandNode.setValueAsync('');

        const result = await executeCommandInnerAsync(commandString);

        await populateResult(commandNode, commandString, dataSources)(result);
    };

    const initializeAsyncInner = async () => {
        if (!dataSources.hasDataSource(DATA_SOURCE_NAME_QDABRA_RULES)) {
            // No qRules
            return;
        }

        const dataSource = getDataSource(
            dataSources,
            DATA_SOURCE_NAME_QDABRA_RULES,
        );

        const loadingNode = getValidNode(
            dataSource,
            '/QdabraRules/@finishedLoading',
            'Could not find finishedLoading field',
        );

        await registerViewNameTracker(dataSource, viewManager);

        logging.logVerbose('Begin finishedLoading');

        try {
            await loadingNode.setValueAsync(true);
        } finally {
            logging.logVerbose('End finishedLoading');
        }
    };

    async function initializeAsync() {
        logging.logVerbose('Begin qRules init');

        try {
            await initializeAsyncInner();
        } finally {
            logging.logVerbose('End qRules init');
        }
    }

    function addCommand(name, definition) {
        if (name in commands) {
            console.warn('There is already a command with the name ' + name);
        }

        commands[name] = definition;
    }

    return {
        executeCommandAsync,
        initializeAsync,
        addCommand,
    };
}

export default qRules;
